import { cn } from "@/lib/cn";
import { motion } from "motion/react";
import type { ReactNode } from "react";
import { useSvgId } from "./isometric";
import type { RoomData } from "./roomData";
import { stepPath } from "./stepPath";

export function RoomCards({
  room,
  announce,
}: {
  room: RoomData;
  announce: boolean;
}) {
  return (
    <div
      aria-live={announce ? "polite" : "off"}
      className="flex flex-col items-center gap-4 text-left xl:pointer-events-none xl:absolute xl:inset-0"
    >
      <Card
        roomId={room.id}
        className="xl:absolute xl:left-0 xl:top-1/2 xl:-translate-y-1/2"
      >
        <h4 className="sr-only">{room.name}</h4>
        <p className="text-sm text-primary">{room.time}</p>
        <div className="mt-4 grid grid-cols-2 gap-2">
          <Tag>{room.occupancy}</Tag>
          <Tag>Door {room.door}</Tag>
        </div>
        <div className="-mx-6 mt-6 flex justify-between border-t border-primary px-6 pt-6 text-sm text-primary">
          <span>{room.alerts ? "Needs Attention" : "All Systems Healthy"}</span>
          <span>
            {room.alerts
              ? `${room.alerts} Alert${room.alerts > 1 ? "s" : ""}`
              : "No Alerts"}
          </span>
        </div>
      </Card>
      <div className="flex w-full flex-col items-center gap-4 xl:absolute xl:right-0 xl:top-1/2 xl:w-80 xl:-translate-y-1/2">
        <Card roomId={room.id}>
          <p className="text-sm text-primary">Room’s energy usage</p>
          <p className="mt-2 text-xl text-primary">{room.kw}kW</p>
          <StepChart values={room.energy} filled />
        </Card>
        <Card roomId={room.id}>
          <div className="flex items-start justify-between gap-4">
            <div className="text-primary">
              <p className="text-sm">Room Thermostat</p>
              <p className="mt-2 flex items-baseline gap-3">
                <span className="text-xl">{room.currentF}°F</span>
                <span className="font-mono text-xs uppercase tracking-caps">
                  Set to {room.setpointF}°F
                </span>
              </p>
            </div>
            <Tag>{room.mode}</Tag>
          </div>
          <StepChart values={room.tempSeries} reference={room.setpointSeries} />
        </Card>
      </div>
    </div>
  );
}

function Card({
  roomId,
  className,
  children,
}: {
  roomId: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      className={cn(
        "pointer-events-auto w-full overflow-hidden rounded-2xl bg-primary p-6 shadow-lg ring-1 ring-black/5 xl:w-80",
        className,
      )}
    >
      <motion.div
        key={roomId}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </div>
  );
}

function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-lg border border-current p-2.5 text-center font-mono text-xs uppercase leading-none tracking-caps text-primary">
      {children}
    </span>
  );
}

const CHART = { width: 320, height: 56 };

function StepChart({
  values,
  reference = [],
  filled,
}: {
  values: number[];
  reference?: number[];
  filled?: boolean;
}) {
  const fillId = useSvgId("chart-fill");
  const all = [...values, ...reference];
  const pad = Math.max(1, (Math.max(...all) - Math.min(...all)) / 2);
  const domain: [number, number] = [
    Math.min(...all) - pad,
    Math.max(...all) + pad,
  ];
  const line = stepPath(values, domain, CHART.width, CHART.height);
  return (
    <div className="-mx-6 -mb-6 mt-4">
      <svg
        viewBox={`0 0 ${CHART.width} ${CHART.height}`}
        preserveAspectRatio="none"
        aria-hidden
        className="block h-14 w-full fill-none stroke-illustration"
      >
        {filled && (
          <>
            <linearGradient
              id={fillId}
              x1="0"
              y1="0"
              x2="0"
              y2="1"
              className="text-line"
            >
              <stop stopColor="currentColor" />
              <stop offset="1" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
            <path
              d={`${line}V${CHART.height}H0Z`}
              fill={`url(#${fillId})`}
              stroke="none"
            />
          </>
        )}
        {reference.length > 0 && (
          <path
            d={stepPath(reference, domain, CHART.width, CHART.height)}
            strokeWidth={0.5}
            strokeDasharray="1 1"
            vectorEffect="non-scaling-stroke"
          />
        )}
        <path d={line} vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}
